import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, Transition as _Transition, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "text-sm" }
const _hoisted_2 = {
  type: "button",
  class: "flex items-center justify-between rounded-md py-1.5 px-3 text-xs text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6 w-full"
}
const _hoisted_3 = { class: "rounded-lg shadow-lg min-w-[220px] ring-1 bg-white ring-gray-300 text-sm px-1 py-2" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "px-5 py-1.5 hover:bg-gray-100 rounded-md cursor-pointer" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "flex cursor-pointer items-center w-fit mt-6" }
const _hoisted_8 = { class: "ml-2" }

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { ChevronUpDownIcon } from "@heroicons/vue/24/outline";
import VueDatePicker from "@vuepic/vue-datepicker";
import format from "date-fns/format";
import { ref, PropType, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import { ProcessReport } from "@/types/Process";


export default /*#__PURE__*/_defineComponent({
  __name: 'WorkingHourCurveConfig',
  props: {
  report: {
    type: Object as PropType<ProcessReport>,
    required: true,
  },
},
  emits: ["refetch", "configChange"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const { t, locale } = useI18n();
const emits = __emit;

const absoluteDateRange = ref<[Date, Date]>();
const dateRange = ref(0);
const timeRangeLabel = ref("");
const currentMode = ref<"absolute" | "preset">("preset");
const showMeanWorkingHours = ref(true);

const presetRanges = computed(() => {
  const month = 1000 * 60 * 60 * 24 * 30;
  const week = 1000 * 60 * 60 * 24 * 7;

  return [
    {
      label: t("time.last_week"),
      value: week,
    },
    {
      label: t("time.last_month"),
      value: month * 1,
    },
    {
      label: t("time.last_month", 3),
      value: month * 3,
    },
    {
      label: t("time.last_month", 6),
      value: month * 6,
    },
    {
      label: t("time.last_month", 12),
      value: month * 12,
    },
  ];
});

const generateTimeRangeLabel = () => {
  if (currentMode.value === "preset") {
    return presetRanges.value.find((range) => range.value === dateRange.value)?.label || "";
  }

  if (currentMode.value === "absolute") {
    return absoluteDateRange.value
      ? `${format(absoluteDateRange.value[0], "dd.MM.yyyy")} - ${format(
          absoluteDateRange.value[1],
          "dd.MM.yyyy",
        )}`
      : t("time.all_time");
  }

  return "";
};

watch(
  () => props.report,
  (report) => {
    const plotConfig = (report as unknown as ProcessReport<"working_hour_curve">).plot.config;

    if (typeof plotConfig.start_date === "number") {
      currentMode.value = "preset";
      dateRange.value = plotConfig.start_date;
    } else {
      currentMode.value = "absolute";

      if (plotConfig.start_date && plotConfig.end_date) {
        absoluteDateRange.value = [new Date(plotConfig.start_date), new Date(plotConfig.end_date)];
      } else {
        absoluteDateRange.value = undefined;
      }
    }

    showMeanWorkingHours.value = plotConfig.show_mean;
    timeRangeLabel.value = generateTimeRangeLabel();
  },
  { immediate: true },
);

const handleDateChange = (value?: [Date, Date]) => {
  currentMode.value = "absolute";
  absoluteDateRange.value = value;
  timeRangeLabel.value = generateTimeRangeLabel();

  emits("refetch", getConfig());
};

const handleRangeChange = (value: number) => {
  currentMode.value = "preset";
  dateRange.value = value;
  timeRangeLabel.value = generateTimeRangeLabel();

  emits("refetch", getConfig());
};

const getConfig = () => {
  const payload: Record<string, unknown> = {
    show_mean: showMeanWorkingHours.value,
  };

  if (currentMode.value === "preset") {
    payload.start_date = dateRange.value;
    payload.end_date = undefined;
  } else {
    payload.start_date = absoluteDateRange.value
      ? format(absoluteDateRange.value[0], "yyyy-MM-dd")
      : undefined;
    payload.end_date = absoluteDateRange.value
      ? format(absoluteDateRange.value[1], "yyyy-MM-dd")
      : undefined;
  }

  return payload;
};

__expose({
  getConfig,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("p", _hoisted_1, _toDisplayString(_unref(t)("buttons.time_range")), 1),
    _createVNode(_unref(Popover), { class: "relative" }, {
      default: _withCtx(() => [
        _createVNode(_unref(PopoverButton), { class: "w-full" }, {
          default: _withCtx(() => [
            _createElementVNode("button", _hoisted_2, [
              _createElementVNode("span", null, _toDisplayString(timeRangeLabel.value), 1),
              _createVNode(_unref(ChevronUpDownIcon), {
                class: "ml-2 h-5 w-5 text-gray-400",
                "aria-hidden": "true"
              })
            ])
          ]),
          _: 1
        }),
        _createVNode(_Transition, {
          "enter-active-class": "transition duration-200 ease-out",
          "enter-from-class": "translate-y-1 opacity-0",
          "enter-to-class": "translate-y-0 opacity-100",
          "leave-active-class": "transition duration-150 ease-in",
          "leave-from-class": "translate-y-0 opacity-100",
          "leave-to-class": "translate-y-1 opacity-0"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(PopoverPanel), { class: "absolute left-0 z-10 mt-3" }, {
              default: _withCtx(({ close }) => [
                _createElementVNode("div", _hoisted_3, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(presetRanges.value, (preset) => {
                    return (_openBlock(), _createElementBlock("p", {
                      class: "px-5 py-1.5 hover:bg-gray-100 rounded-md cursor-pointer",
                      onClick: ($event: any) => (handleRangeChange(preset.value), close()),
                      key: preset.label
                    }, _toDisplayString(preset.label), 9, _hoisted_4))
                  }), 128)),
                  _createVNode(_unref(VueDatePicker), {
                    modelValue: absoluteDateRange.value,
                    "onUpdate:modelValue": [
                      _cache[0] || (_cache[0] = ($event: any) => ((absoluteDateRange).value = $event)),
                      ($event: any) => (handleDateChange($event), close())
                    ],
                    columns: 4,
                    "enable-time-picker": false,
                    placeholder: `${_unref(t)('report.bulk_export.main_label')}`,
                    locale: _unref(locale),
                    format: "dd.MM.yyyy",
                    "auto-apply": "",
                    range: ""
                  }, {
                    trigger: _withCtx(() => [
                      _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(t)("analytics.reports.absolute_date")), 1)
                    ]),
                    _: 2
                  }, 1032, ["modelValue", "placeholder", "locale", "onUpdate:modelValue"]),
                  _createElementVNode("p", {
                    class: "px-5 py-1.5 hover:bg-gray-100 rounded-md cursor-pointer",
                    onClick: ($event: any) => (handleDateChange(), close())
                  }, _toDisplayString(_unref(t)("time.all_time")), 9, _hoisted_6)
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createElementVNode("label", _hoisted_7, [
      _withDirectives(_createElementVNode("input", {
        type: "checkbox",
        class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer",
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showMeanWorkingHours).value = $event)),
        onChange: _cache[2] || (_cache[2] = ($event: any) => (emits('configChange', getConfig())))
      }, null, 544), [
        [_vModelCheckbox, showMeanWorkingHours.value]
      ]),
      _createElementVNode("span", _hoisted_8, _toDisplayString(_unref(t)("analytics.reports.show_mean_working_hours")), 1)
    ])
  ], 64))
}
}

})